import { readTypedDollars } from "./figures";
import type { Outcome } from "./types";
import { HOURS } from "./window";

/**
 * "The nudge" (Composer §4.5).
 *
 * Auto-learn, the Analyst, attribution and graduation all starve without
 * outcome labels, and the spec's answer is to keep the debt small: threads
 * that went quiet without an outcome surface for tagging, "never more than a
 * few taps of debt".
 *
 * Two things this predicate deliberately does NOT do:
 *
 * - It never supplies a label. §4.5's honesty rule is that untagged is not
 *   lost; an unknown outcome stays unknown and is excluded from learning. The
 *   ghost auto-label (21 days after the last outbound, 10 for repairs) is a
 *   separate, separately specced write and lives in the worker, not here.
 * - It never looks at one direction only. A customer who went silent after the
 *   shop's reply two days ago is being waited on, not forgotten.
 *
 * SPEC GAP (FINDINGS TA-13): the spec says "went quiet" and "weekly digest"
 * without a number. Seven days is implemented as the conservative reading of
 * "weekly". Awaiting Kabir.
 */
export const QUIET_WITHOUT_OUTCOME_MS = 7 * 24 * HOURS;

export interface OutcomeTagInput {
  readonly lastCustomerMessageAt: Date | null;
  readonly lastOutboundAt: Date | null;
  /** The latest unreversed outcome label on the thread, or null if none. */
  readonly outcome: string | null;
  readonly now: Date;
  readonly quietMs?: number;
}

/**
 * Pure function, no clock, no IO. The inbox filter, the overview tile and any
 * future digest call THIS, so "needs a tag" has exactly one definition.
 */
export function needsOutcomeTag(input: OutcomeTagInput): boolean {
  // `ongoing` is the enum's word for unknown (§4.5). It is the absence of a
  // label, not a label, so it does not settle anything.
  if (input.outcome !== null && input.outcome !== "ongoing") return false;

  const stamps = [input.lastCustomerMessageAt, input.lastOutboundAt].filter(
    (d): d is Date => d !== null,
  );
  // Nothing was ever said in either direction. That is not a conversation
  // that went quiet; it is a thread with no conversation in it.
  if (stamps.length === 0) return false;

  const latest = Math.max(...stamps.map((d) => d.getTime()));
  const elapsed = input.now.getTime() - latest;
  // Clock skew fails towards silence. A nag issued because a webhook arrived
  // ahead of our own clock is a nag nobody can explain.
  if (elapsed < 0) return false;

  return elapsed >= (input.quietMs ?? QUIET_WITHOUT_OUTCOME_MS);
}

/**
 * The labels an owner records (Composer 4.5, verbatim): "One-tap outcome chips
 * on every thread: `Sold` `Booked` `Deposit` `Lost`, one tap, optional amount
 * on Sold/Deposit."
 *
 * One list for every surface that records a tap: the inbox's chips
 * (OutcomeChips.tsx), its server action, and `pnpm outcomes` on the ops task
 * (L4), so the terminal can never offer a label the inbox does not. `ghosted`
 * is not here because the machine writes it (ghostLabel.ts) and a tap
 * withdraws it; `ongoing` is not here because it is the absence of a label
 * (the honesty rule above), and a chip for it would make untagged look tagged.
 * Adding a label is a spec change, and TA-279 Q1 (a DM-visible label such as
 * handed-off-to-visit) is Kabir's.
 */
export const OUTCOME_CHIP_LABELS = ["sold", "booked", "deposit", "lost"] as const satisfies readonly Outcome[];
export type OutcomeChipLabel = (typeof OUTCOME_CHIP_LABELS)[number];

export const isOutcomeChipLabel = (word: string): word is OutcomeChipLabel =>
  (OUTCOME_CHIP_LABELS as readonly string[]).includes(word);

/** "optional amount on Sold/Deposit": the two labels an amount may ride on. */
export const OUTCOME_AMOUNT_LABELS: readonly OutcomeChipLabel[] = ["sold", "deposit"];

const AMOUNT_LABEL_REFUSAL = (label: string): string =>
  `An amount is recorded on sold and deposit only (Composer 4.5: "optional amount on Sold/Deposit"), ` +
  `and this outcome is ${label}. Record it without an amount.`;

const ZERO_REFUSAL =
  "The amount is zero. Leave the amount out instead: an outcome with no amount says nothing about money, " +
  "and a zero would be summed as a sale for nothing.";

const NEGATIVE_REFUSAL = "The amount is negative. An amount is what the sale or the deposit came to, above zero.";

/**
 * Why `amountCents` may not be recorded on `label`, or null. The guard
 * `recordOutcome` applies before it writes anything, so every surface meets
 * it, whatever it parsed the amount from.
 *
 * Zero is refused (L4's conservative reading, Kabir's to rule): the spec makes
 * the amount optional, so "no amount" has its own spelling, and a zero summed
 * into revenue is a figure nobody stated. The amount is integer cents from the
 * money module (F-16); anything else is refused rather than rounded.
 */
export function outcomeAmountRefusal(label: string, amountCents: number): string | null {
  if (!(OUTCOME_AMOUNT_LABELS as readonly string[]).includes(label)) return AMOUNT_LABEL_REFUSAL(label);
  if (!Number.isSafeInteger(amountCents)) {
    return "The amount is not a whole number of cents that can be held exactly, so it is not recorded rather than rounded.";
  }
  if (amountCents < 0) return NEGATIVE_REFUSAL;
  if (amountCents === 0) return ZERO_REFUSAL;
  return null;
}

export type OutcomeAmountRead =
  | { readonly ok: true; readonly amountCents: number }
  | { readonly ok: false; readonly refusal: string };

/**
 * An amount an owner typed for `label`, read by the money module
 * (`readTypedDollars`, F-16) into integer cents, or the sentence that says why
 * not. The label is judged first, so booked and lost refuse any amount. The
 * sentences never repeat what was typed: on the ops task every line lands in
 * a log group (TA-428 follow-up), and a mistyped field can hold anything.
 */
export function readOutcomeAmount(label: OutcomeChipLabel, raw: string): OutcomeAmountRead {
  const refuse = (refusal: string): OutcomeAmountRead => ({ ok: false, refusal });
  if (!OUTCOME_AMOUNT_LABELS.includes(label)) return refuse(AMOUNT_LABEL_REFUSAL(label));
  const read = readTypedDollars(raw);
  if (!read.ok) {
    switch (read.reason) {
      case "negative":
        return refuse(NEGATIVE_REFUSAL);
      case "too-large":
        return refuse("The amount is too large to hold exactly in whole cents, so it is not recorded rather than rounded.");
      case "unreadable":
        return refuse(
          "The amount is not one dollar amount this system can read. Write whole dollars (1900 or 1,900), or " +
            'dollars and two digits of cents (1900.50), with or without "$" and with nothing else in the value.',
        );
    }
  }
  const refusal = outcomeAmountRefusal(label, read.cents);
  return refusal === null ? { ok: true, amountCents: read.cents } : refuse(refusal);
}
